import { ISurfaceDimensions, IVenueSurfaceSvgConfig, IVenueClickEvent } from './venue-surface.models';

import { getParsedAttr } from 'src/app/utils/html-dom.utils';
import { Sport } from 'src/app/states/sport/sport.models';

/**
 * Static URLs for supported sports' playing surfaces.
 */
export const SPORT_URLS: Record<string, string> = {
    [Sport.SOCCER]: '/assets/images/SoccerPitch.svg',
    [Sport.HOCKEY]: '/assets/images/HockeyRink.svg'
};

/**
 * Parses raw SVG text to dynamically extract viewBox, dimensions, and field boundaries.
 */
// eslint-disable-next-line complexity
export function parseVenueSurfaceSvg(svgText: string, url: string): IVenueSurfaceSvgConfig | undefined {
    if (typeof DOMParser === 'undefined') {
        return;
    }
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svgEl = doc.querySelector('svg');

        if (!svgEl) {
            return;
        }

        const viewBox = svgEl.getAttribute('viewBox') ?? '';
        const widthAttr = svgEl.getAttribute('width') ?? '';
        const heightAttr = svgEl.getAttribute('height') ?? '';

        const parsedViewBox = viewBox.split(/[\s,]+/).map(parseFloat);
        const viewBoxWidth = parsedViewBox.length === 4 ? parsedViewBox[2] : 0;
        const viewBoxHeight = parsedViewBox.length === 4 ? parsedViewBox[3] : 0;

        const width = parseFloat(widthAttr) || viewBoxWidth;
        const height = parseFloat(heightAttr) || viewBoxHeight;

        const fieldBounds = svgEl.querySelector('#field-bounds');

        if (!fieldBounds) {
            return;
        }

        const fieldLeft = getParsedAttr(fieldBounds, 'x');
        const fieldTop = getParsedAttr(fieldBounds, 'y');
        const fieldWidth = getParsedAttr(fieldBounds, 'width');
        const fieldHeight = getParsedAttr(fieldBounds, 'height');

        return {
            url,
            viewBox,
            width,
            height,
            fieldLeft,
            fieldTop,
            fieldWidth,
            fieldHeight
        };
    } catch (e) {
        console.error('Failed to parse SVG config:', e);
        return;
    }
}

/**
 * Calculates click coordinates in both SVG viewBox units and real-world meters.
 *
 * --- Coordinate Translation Pipeline ---
 * 1. Screen Click (clientX, clientY) -> Translated using the SVG's Current Transformation Matrix (CTM)
 *    to exact virtual viewBox coordinates (x, y) where x is [0..viewBoxWidth] and y is [0..viewBoxHeight].
 * 2. If real-world field dimensions are provided:
 *    - Shift the coordinate to the field's top-left corner: `x - fieldLeft`
 *    - Normalize to a fraction (0.0 to 1.0) of the playable field: `(x - fieldLeft) / fieldWidth`
 *    - Map the fraction to real-world meters using a midfield-centered origin:
 *      - `realX = (fractionX - 0.5) * fieldLength` (ranges from -length/2 to +length/2)
 *      - `realY = (fractionY - 0.5) * fieldWidth` (ranges from -width/2 to +width/2)
 *
 * The real-world origin (0,0) is located at the exact center (midfield/center-ice) of the playable field,
 * with +X running left-to-right (-length/2 to +length/2) and +Y running top-to-bottom (-width/2 to +width/2).
 */
export function getVenueSurfaceCoords(
    event: MouseEvent,
    svgElement: SVGSVGElement,
    config: IVenueSurfaceSvgConfig,
    fieldDimensions?: ISurfaceDimensions | null
): IVenueClickEvent {
    let x = 0;
    let y = 0;

    // Use SVG Current Transformation Matrix (CTM) for exact viewBox coordinate calculation.
    //
    // Why CTM?
    // - A screen click gives us coordinates in screen pixels (clientX, clientY).
    // - The SVG is responsive and may be scaled, zoomed, or padded (letterboxed/pillarboxed) on different screens.
    // - `getScreenCTM()` returns the matrix the browser uses to map virtual viewBox coordinates to screen pixels.
    // - By taking the inverse of this matrix (`ctm.inverse()`) and multiplying it by our screen point,
    //   we map the screen click back to the exact virtual viewBox unit coordinate, regardless of responsive scaling.
    if (typeof svgElement.createSVGPoint === 'function' && typeof svgElement.getScreenCTM === 'function') {
        const point = svgElement.createSVGPoint();
        point.x = event.clientX;
        point.y = event.clientY;

        const ctm = svgElement.getScreenCTM();
        if (ctm) {
            const svgPoint = point.matrixTransform(ctm.inverse());
            x = Math.max(0, Math.min(config.width, svgPoint.x));
            y = Math.max(0, Math.min(config.height, svgPoint.y));
        }
    } else {
        // Fallback calculation using bounding rect dimensions if CTM is not supported.
        // This maps the click as a percentage of the screen bounding box to the virtual coordinate width/height.
        const rect = svgElement.getBoundingClientRect();
        x = Math.max(0, Math.min(config.width, ((event.clientX - rect.left) / rect.width) * config.width));
        y = Math.max(0, Math.min(config.height, ((event.clientY - rect.top) / rect.height) * config.height));
    }

    const clickEvent: IVenueClickEvent = { x, y, realX: 0, realY: 0 };

    if (fieldDimensions) {
        // Position within the field rect as a fraction (0..1), clamped to the pitch
        const fx = Math.max(0, Math.min(1, (x - config.fieldLeft) / config.fieldWidth));
        const fy = Math.max(0, Math.min(1, (y - config.fieldTop) / config.fieldHeight));

        // Midfield-centered coordinates (0,0 at center of the field)
        clickEvent.realX = (fx - 0.5) * fieldDimensions.length;
        clickEvent.realY = (fy - 0.5) * fieldDimensions.width;
    }

    return clickEvent;
}

/**
 * Converts real-world coordinates (meters, midfield-centered) into SVG viewBox units.
 *
 * @param realX
 * @param realY
 * @param config
 * @param fieldDimensions
 * @returns
 */
export function getVenueSurfaceSvgCoords(
    realX: number,
    realY: number,
    config: IVenueSurfaceSvgConfig,
    fieldDimensions: ISurfaceDimensions
): { x: number; y: number } {
    // Convert midfield-centered meters back to a field fraction (0..1), clamped to the pitch
    const fx = Math.max(0, Math.min(1, realX / fieldDimensions.length + 0.5));
    const fy = Math.max(0, Math.min(1, realY / fieldDimensions.width + 0.5));

    // Map the fraction into the field rect within the SVG viewBox
    const x = config.fieldLeft + fx * config.fieldWidth;
    const y = config.fieldTop + fy * config.fieldHeight;

    return { x, y };
}
